<div class="bg-white/40 dark:bg-dark/10 rounded-3xl">
  <div class="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-4">
    <div>
      <h1 class="text-base font-semibold text-black dark:text-white">{{ pageTitle }}</h1>
      <p class="text-sm text-gray-500 dark:text-white-dark">{{ pageSubtitle }}</p>
    </div>
    @if (!loading && !loadError && smtp) {
      @if (!editMode && (P.SMTP_SETTINGS_UPDATE | hasPermission)) {
        <button
          type="button"
          class="btn bg-white text-black hover:bg-[#303030] hover:text-white inline-flex items-center gap-2 py-3 px-4 rounded-full shadow-none"
          (click)="startEdit()"
        >
          <icon-edit class="h-4 w-4" />
          Edit
        </button>
      } @else if (editMode && (P.SMTP_SETTINGS_UPDATE | hasPermission)) {
        <button
          type="button"
          class="btn bg-white text-black hover:bg-[#303030] hover:text-white inline-flex items-center gap-2 py-3 px-4 rounded-full shadow-none"
          (click)="cancelEdit()"
          [disabled]="saving"
        >
          Cancel
        </button>
      }
    }
  </div>

  <div class="rounded-3xl border border-white-light/80 bg-white p-4 shadow-sm dark:border-[#191e3a] dark:bg-[#0e1726]">
    @if (loadError) {
      <div class="mb-3 flex flex-wrap items-center gap-2 text-sm text-danger">
        <span>{{ loadError }}</span>
        <button type="button" class="btn btn-sm rounded-full px-3 py-1" (click)="loadSmtpSettings()">
          Retry
        </button>
      </div>
    }

    @if (loading) {
      <p class="text-sm text-gray-500 dark:text-white-dark">Loading SMTP settings…</p>
    } @else if (!loadError && smtp && !editMode && (P.SMTP_SETTINGS_LIST | hasPermission)) {
      @if (!smtp.use_custom_smtp) {
        <div
          class="rounded-2xl border border-white-light/80 bg-white-light/40 p-3 text-sm dark:border-[#1b2e4b] dark:bg-white/5 dark:text-white-dark"
        >
          Custom SMTP is off. Outbound mail uses
          <span class="font-semibold text-black dark:text-white">.env</span>
          (<code class="rounded bg-black/5 px-1 py-0.5 text-xs dark:bg-white/10">EMAIL_SERVICE_*</code>).
        </div>
      } @else {
        <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-white-dark">Mode</p>
            <p class="mt-1 text-base font-bold text-black dark:text-white">Custom SMTP</p>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-white-dark">TLS / SSL</p>
            <p class="mt-1 text-base font-bold text-black dark:text-white">{{ smtp.secure ? 'Yes' : 'No' }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-white-dark">From email</p>
            <p class="mt-1 text-base font-bold text-black dark:text-white">{{ display(smtp.from_email) }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-white-dark">SMTP host</p>
            <p class="mt-1 text-base font-bold text-black dark:text-white">{{ display(smtp.host) }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-white-dark">Port</p>
            <p class="mt-1 text-base font-bold text-black dark:text-white">{{ display(smtp.port) }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-white-dark">Login ID</p>
            <p class="mt-1 text-base font-bold text-black dark:text-white">{{ display(smtp.auth_user) }}</p>
          </div>
          <div>
            <p class="text-xs font-medium text-gray-500 dark:text-white-dark">Password</p>
            <p class="mt-1 text-base font-bold text-black dark:text-white">
              {{ smtp.password_configured ? 'Saved (encrypted)' : 'Not set' }}
            </p>
          </div>
        </div>
      }
    } @else if (editMode && smtp && (P.SMTP_SETTINGS_UPDATE | hasPermission)) {
      <form class="grid grid-cols-1 gap-5 md:grid-cols-2" (ngSubmit)="submit($event)">
        <div class="md:col-span-2 flex items-center gap-3">
          <input
            id="smtp-use-custom"
            type="checkbox"
            class="form-checkbox"
            [(ngModel)]="form.use_custom_smtp"
            name="useCustomSmtp"
          />
          <label for="smtp-use-custom" class="text-sm font-medium text-black dark:text-white">
            Use custom SMTP (instead of .env)
          </label>
        </div>

        @if (form.use_custom_smtp) {
          <p class="md:col-span-2 text-xs text-gray-500 dark:text-gray-400">
            <span class="font-semibold text-red-600" aria-hidden="true">*</span> Required field
          </p>

          <div>
            <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-white-dark" for="smtp-from">
              From email <span class="font-semibold text-red-600" aria-hidden="true">*</span>
            </label>
            <input
              id="smtp-from"
              type="email"
              class="form-input"
              [(ngModel)]="form.from_email"
              (ngModelChange)="clearFieldError('from_email')"
              name="from_email"
              autocomplete="off"
              required
              aria-required="true"
            />
            @if (fieldError('from_email')) {
              <p class="mt-1 text-xs text-danger">{{ fieldError('from_email') }}</p>
            }
          </div>

          <div>
            <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-white-dark" for="smtp-server">
              SMTP host <span class="font-semibold text-red-600" aria-hidden="true">*</span>
            </label>
            <input
              id="smtp-server"
              type="text"
              class="form-input"
              [(ngModel)]="form.host"
              (ngModelChange)="clearFieldError('host')"
              name="host"
              autocomplete="off"
              required
              aria-required="true"
            />
            @if (fieldError('host')) {
              <p class="mt-1 text-xs text-danger">{{ fieldError('host') }}</p>
            }
          </div>

          <div>
            <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-white-dark" for="smtp-port">
              Port <span class="font-semibold text-red-600" aria-hidden="true">*</span>
            </label>
            <input
              id="smtp-port"
              type="text"
              class="form-input"
              [(ngModel)]="form.port"
              (ngModelChange)="clearFieldError('port')"
              name="port"
              inputmode="numeric"
              required
              aria-required="true"
            />
            @if (fieldError('port')) {
              <p class="mt-1 text-xs text-danger">{{ fieldError('port') }}</p>
            }
          </div>

          <div class="flex items-end pb-2">
            <label class="inline-flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" class="form-checkbox" [(ngModel)]="form.secure" name="secure" />
              Use TLS / SSL (secure)
            </label>
          </div>

          <div>
            <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-white-dark" for="smtp-login">
              Login ID <span class="font-semibold text-red-600" aria-hidden="true">*</span>
            </label>
            <input
              id="smtp-login"
              type="text"
              class="form-input"
              [(ngModel)]="form.auth_user"
              (ngModelChange)="clearFieldError('auth_user')"
              name="auth_user"
              autocomplete="off"
              required
              aria-required="true"
            />
            @if (fieldError('auth_user')) {
              <p class="mt-1 text-xs text-danger">{{ fieldError('auth_user') }}</p>
            }
          </div>

          <div class="md:col-span-2">
            <label class="mb-1 block text-xs font-medium text-gray-500 dark:text-white-dark" for="smtp-pass">
              Password
              @if (!smtp.password_configured) {
                <span class="font-semibold text-red-600" aria-hidden="true">*</span>
              }
            </label>
            <div class="relative">
              <input
                id="smtp-pass"
                [type]="showPassword ? 'text' : 'password'"
                class="form-input pr-10"
                [(ngModel)]="form.auth_password"
                (ngModelChange)="clearFieldError('auth_password')"
                name="auth_password"
                autocomplete="off"
                [placeholder]="
                  smtp.password_configured
                    ? 'Leave blank to keep the current secret'
                    : 'Required when enabling custom SMTP'
                "
              />
              <button
                type="button"
                class="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-gray-500 hover:bg-white-light/80 hover:text-primary dark:hover:bg-white/10"
                (click)="togglePasswordVisible()"
                [attr.aria-label]="showPassword ? 'Hide password' : 'Show password'"
              >
                <icon-eye class="h-5 w-5" />
              </button>
            </div>
            @if (fieldError('auth_password')) {
              <p class="mt-1 text-xs text-danger">{{ fieldError('auth_password') }}</p>
            }
            <p class="mt-1 text-xs text-gray-500 dark:text-white-dark">
              Stored encrypted. Leave blank to keep the existing password when updating other fields.
            </p>
          </div>
        } @else {
          <div
            class="md:col-span-2 rounded-2xl border border-dashed border-gray-300 p-3 text-sm dark:border-[#1b2e4b] dark:text-white-dark"
          >
            Mail will be sent using <span class="font-semibold">EMAIL_SERVICE_*</span> from the server environment.
          </div>
        }

        <div class="md:col-span-2">
          @if (saveError) {
            <p class="mb-3 text-sm text-danger">{{ saveError }}</p>
          }
          <button
            type="submit"
            [disabled]="saving"
            class="btn rounded-full bg-[#303030] px-8 font-semibold text-white shadow-lg disabled:opacity-50"
          >
            {{ saving ? 'Saving...' : 'Save changes' }}
          </button>
        </div>
      </form>
    } @else if (!loading && !loadError && !editMode && !(P.SMTP_SETTINGS_LIST | hasPermission) && !(P.SMTP_SETTINGS_UPDATE | hasPermission)) {
      <p class="text-sm text-gray-500 dark:text-white-dark">
        You do not have permission to view SMTP settings.
      </p>
    }
  </div>
</div>
